import { motion, useReducedMotion } from 'framer-motion'
import { useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Alert, Box, PasswordInput, Stack, Text, TextInput, Title } from '@mantine/core'
import { useAuth } from '@/lib/auth-context'
import { ApiError } from '@/lib/api'
import { ActionButton } from '@/components/ui/ActionButton'

export function LoginPage() {
  const { signIn, user } = useAuth()
  const location = useLocation()
  /** Куда вернуть после входа: сохранённый URL (включая query) или дашборд. Не ведём на /login — избегаем цикла. */
  const from = (() => {
    const raw = (location.state as { from?: string } | null)?.from?.trim()
    if (!raw || raw === '/login') return '/dashboard'
    return raw.startsWith('/') ? raw : '/dashboard'
  })()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const reduceMotion = useReducedMotion()

  if (user) {
    return <Navigate to={from} replace />
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await signIn(email.trim(), password)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось войти')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box
      mih="100dvh"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem 1rem',
        position: 'relative',
        overflow: 'hidden',
        background: 'var(--mantine-color-body)',
      }}
    >
      <Box
        aria-hidden
        className="login-page-ambient"
        style={{
          position: 'absolute',
          inset: '-40%',
          pointerEvents: 'none',
        }}
      />
      <motion.div
        initial={{ opacity: reduceMotion ? 1 : 0, y: reduceMotion ? 0 : 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: reduceMotion ? 0 : 0.42,
          ease: [0.22, 1, 0.36, 1],
        }}
        style={{ width: '100%', maxWidth: 420 }}
      >
      <Box
        component="form"
        onSubmit={onSubmit}
        w="100%"
        p="xl"
        style={{
          position: 'relative',
          borderRadius: 'var(--mantine-radius-lg)',
          border: '1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))',
          background: 'light-dark(var(--mantine-color-white), var(--mantine-color-dark-7))',
          boxShadow: 'var(--mantine-shadow-xl)',
        }}
      >
        <Stack gap="xl">
          <Stack gap="xs">
            <Text size="xs" fw={700} tt="uppercase" lts="0.12em" c="var(--mantine-color-brand-filled)">
              Операторская панель
            </Text>
            <Title order={1} size="h2" fw={700} style={{ letterSpacing: '-0.02em' }}>
              Вход в систему
            </Title>
            <Text size="sm" c="dimmed" fw={500} lts="0.04em">
              Bureau Of Business Assistance
            </Text>
            <Text size="sm" c="dimmed" mt="xs" lh={1.45}>
              Единая линия обращений: ответы клиентам, статусы и история переписки.
            </Text>
          </Stack>

          <Stack gap="md">
            {error ? (
              <Alert color="red" variant="light" title="Не удалось войти">
                {error}
              </Alert>
            ) : null}

            <TextInput
              label="Email"
              type="email"
              name="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
            />

            <PasswordInput
              label="Пароль"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />

            <ActionButton
              action="continue"
              type="submit"
              fullWidth
              loading={loading}
              mt="xs"
            />
          </Stack>
        </Stack>
      </Box>
      </motion.div>
    </Box>
  )
}
